import React, { Suspense, lazy, useMemo } from 'react';
import { Plus, Minus, Maximize2 } from 'lucide-react';
import { usePanZoom } from './usePanZoom';
import { PlanView, ElevationView, planVB, elevVB } from './views';

export const Scene3D = lazy(() => import('./three/Scene3D'));

export const VIEWS = {
  plan: { label: 'Plan', Comp: PlanView, vb: (L) => planVB(L) },
  elevation: { label: 'Élévation', Comp: ElevationView, vb: (L, v, wall) => elevVB(L, wall) },
  perspective: { label: 'Vue 3D' },
};

const ZoomBtn = ({ onClick, children, id }) => (
  <button type="button" onClick={onClick} data-testid={id}
    className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a]/85 text-[#d9c3a0] hover:bg-[#a68966] hover:text-[#1a1a1a] transition-colors duration-200 backdrop-blur-md">
    {children}
  </button>
);

export const Loading3D = () => (
  <div className="relative w-full h-full bg-[#efebe4] overflow-hidden" data-testid="scene3d-loading">
    <div className="absolute top-0 left-0 h-[2px] w-1/3 bg-[#a68966] animate-[pulse_1.6s_ease-in-out_infinite]" />
    <p className="absolute inset-0 flex items-center justify-center text-[11px] uppercase tracking-[0.3em] text-[#1a1a1a]/50">Préparation de la scène 3D</p>
  </div>
);

function SvgCanvas({ kind, L, c, sel, onPick, technical, view = 0, p, controls = true, testid, wall = 'A' }) {
  const V = VIEWS[kind];
  const w = L.walls.includes(wall) ? wall : 'A';
  const pz = usePanZoom(V.vb(L, view, w));
  const cc = useMemo(() => ({ ...c, p, F: (id) => `url(#${p}-${id})` }), [c, p]);
  return (
    <div className="relative w-full h-full" data-testid={testid || `view-${kind}`}>
      <V.Comp L={L} c={cc} sel={sel} onPick={onPick} technical={technical} p={p} vb={pz.vb} svgRef={pz.svgRef} bind={pz.bind} view={view} wall={w} />
      {controls && (
        <div className="absolute right-3 bottom-3 flex flex-col gap-[2px] shadow-lg">
          <ZoomBtn onClick={pz.zoomIn} id={`${kind}-zoom-in`}><Plus className="w-4 h-4" /></ZoomBtn>
          <ZoomBtn onClick={pz.zoomOut} id={`${kind}-zoom-out`}><Minus className="w-4 h-4" /></ZoomBtn>
          <ZoomBtn onClick={pz.reset} id={`${kind}-zoom-reset`}><Maximize2 className="w-4 h-4" /></ZoomBtn>
        </div>
      )}
    </div>
  );
}

export function ViewCanvas(props) {
  if (props.kind !== 'perspective') return <SvgCanvas {...props} />;
  return (
    <Suspense fallback={<Loading3D />}>
      <Scene3D L={props.L} c={props.c} sel={props.sel} onPick={props.onPick} controls={props.controls !== false} testid={props.testid} panel={props.panel} />
    </Suspense>
  );
}
